/**
 * /borrower-portal-users — admin view of every borrower-portal user.
 *
 * Visual language matches the Users screen (avatar status dots, rounded filter
 * chips, custom footer/pager, shared Support design tokens). The behavioural
 * twist comes from the Lock Queue: each row is an AG Grid **master/detail** row
 * that expands to a nested table of that borrower's loans, each with a "View
 * Details" action that opens the loan in the drilldown (/loan/:dealId).
 *
 * Data: useCachedBorrowerPortalUsers() (see useCachedSubmissions.js) — fetches
 * `borrower_portal_admin/all_borrower_portal_users` once, caches it in the
 * cachedData reducer, and reuses it on later mounts (fetch-once-then-cache, the
 * same contract as useCachedLoanPipeline; this is what removed the old refetch
 * loop). An empty or failed read just yields an empty list.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry } from "ag-grid-community";
import { MasterDetailModule } from "ag-grid-enterprise";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import InputBase from "@mui/material/InputBase";
import RefreshRounded from "@mui/icons-material/Refresh";
import SearchRounded from "@mui/icons-material/SearchRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";

import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import SideDrawer from "../../components/modals/SideDrawer";
import JsonPreview from "../../components/common/JsonPreview";
import { useCachedBorrowerPortalUsers } from "../../hooks/useCachedSubmissions";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import {
  formatDateTime,
  normalizeIsoStringToUtc,
} from "../../_src_shared/utils/functions/formatDateTime";
import formatDollar from "../../_src_shared/utils/functions/formatDollar";
import { formatPercent } from "../../utils/numbers/formatPercent";
import { T, avatarColor, initialsFromName } from "../SupportScreen/components/supportUi";

// Master/detail (expandable rows) is an enterprise feature — register it here;
// AgGridTableWrapper only registers the community bundle.
ModuleRegistry.registerModules([MasterDetailModule]);

const ALL = "__all__";
const PAGE_SIZES = [15, 25, 50, 100];

// ── helpers ──────────────────────────────────────────────────────────────────
const fullName = (u) =>
  (u?.fullName || `${u?.firstName || ""} ${u?.lastName || ""}`).trim() || "—";
const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const userId = (u) => String(u?._id ?? u?.id ?? u?.emailAddress ?? "");

// Backend timestamps are UTC but often arrive WITHOUT a Z/offset (e.g.
// "2026-05-05T12:19:21.799000"), which new Date() would mis-parse as local time.
// Normalize to UTC first (same rule as formatDateTime) for all time math.
const dateMs = (v) => {
  if (!v) return 0;
  const t = new Date(
    typeof v === "string" ? normalizeIsoStringToUtc(v) : v,
  ).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const timeAgo = (s) => {
  const ms = dateMs(s);
  if (!ms) return "—";
  const diff = (Date.now() - ms) / 1000;
  if (diff < 0) return formatDateTime(s, "date");
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 172800) return "Yesterday";
  return formatDateTime(s, "date");
};

const formatPhone = (p) => {
  const d = String(p || "").replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return p || "—";
};

// Borrower-portal lifecycle → a status chip tone. A borrower who has signed in
// is "Active"; one who's been invited but never signed in is "Invited";
// everything else is "Staged".
const borrowerStatus = (u) => {
  if (u?.firstSignInDate || dateMs(u?.lastSignInDate))
    return { label: "Active", tone: "green" };
  if (u?.invite_sent || u?.stagingStatus === "invited")
    return { label: "Invited", tone: "amber" };
  return { label: "Staged", tone: "slate" };
};

// Collapse a borrower's termSheets (+ bare dealIds) into one row per loan/deal.
// Prefer the accepted term sheet for the loan summary, else the most-recently
// updated one. dealIds with no term sheet still surface as a (sparse) loan row.
const deriveLoans = (borrower) => {
  const byDeal = new Map();
  for (const ts of asArray(borrower?.termSheets)) {
    const id = ts?.dealId;
    if (!id) continue;
    if (!byDeal.has(id)) byDeal.set(id, []);
    byDeal.get(id).push(ts);
  }
  for (const id of asArray(borrower?.dealIds)) {
    if (!byDeal.has(id)) byDeal.set(id, []);
  }
  return [...byDeal.entries()].map(([dealId, sheets]) => {
    const accepted = sheets.find((s) => s?.isAccepted);
    const primary =
      accepted ||
      [...sheets].sort(
        (a, b) => dateMs(b?.updatedDate) - dateMs(a?.updatedDate),
      )[0];
    const d = primary?.term_sheet_data || {};
    return {
      dealId,
      propertyAddress: asArray(d.propertyAddresses).join("  •  "),
      loanProductType: d.loanProductType || "",
      loanPurpose: d.loanPurpose || "",
      totalLoanAmount: d.totalLoanAmount,
      interestRate: d.interestRate,
      loanToValue: d.loanToValue,
      entityName: d.entityName || "",
      status: primary?.loanStatusAtCreation || "",
      termSheetCount: sheets.length,
      accepted: Boolean(accepted),
    };
  });
};

const loanCount = (u) => deriveLoans(u).length;

const pageItems = (page, pageCount) => {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const set = new Set([1, 2, pageCount, page - 1, page, page + 1]);
  const nums = [...set]
    .filter((n) => n >= 1 && n <= pageCount)
    .sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const n of nums) {
    if (n - prev > 1) out.push("…");
    out.push(n);
    prev = n;
  }
  return out;
};

// ── primitives (mirrors UsersScreen's local set) ─────────────────────────────
const Tag = ({ label, tone = "slate" }) => {
  const tones = {
    slate: { bg: "#f1f4f6", fg: T.ink700 },
    teal: { bg: T.teal50, fg: T.teal600 },
    purple: { bg: "#f3eefe", fg: "#7c3aed" },
    indigo: { bg: "#eef2ff", fg: "#4f46e5" },
    amber: { bg: "#fef3e2", fg: T.amber },
    green: { bg: "#effaf2", fg: T.green },
    red: { bg: "#fdecec", fg: T.red },
  };
  const m = tones[tone] || tones.slate;
  return (
    <Box
      component="span"
      sx={{
        fontSize: "11.5px",
        fontWeight: 600,
        color: m.fg,
        background: m.bg,
        borderRadius: "6px",
        px: "8px",
        py: "3px",
        whiteSpace: "nowrap",
        lineHeight: 1.6,
        flexShrink: 0,
      }}
    >
      {label}
    </Box>
  );
};

const TagCell = ({ tags, title }) => {
  if (!tags.length)
    return (
      <Box component="span" sx={{ color: T.ink300 }}>
        —
      </Box>
    );
  const fade = "linear-gradient(to right, #000 calc(100% - 16px), transparent)";
  return (
    <Box
      title={title}
      sx={{
        display: "flex",
        gap: "5px",
        alignItems: "center",
        height: "100%",
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        maskImage: fade,
        WebkitMaskImage: fade,
      }}
    >
      {tags}
    </Box>
  );
};

const AVATAR_SIZE = 34;

const AvatarDot = ({ email, name, status }) => (
  <Box
    sx={{
      position: "relative",
      flexShrink: 0,
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
    }}
  >
    <Box
      sx={{
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "12.5px",
        fontWeight: 700,
        lineHeight: 1,
        letterSpacing: ".02em",
        color: "#fff",
        userSelect: "none",
        background: avatarColor(email || name),
      }}
    >
      {initialsFromName(name || email)}
    </Box>
    {status && (
      <Box
        sx={{
          position: "absolute",
          right: 0,
          bottom: 0,
          width: 10,
          height: 10,
          borderRadius: "50%",
          border: "2px solid #fff",
          background: status === "online" ? T.green : T.ink300,
        }}
      />
    )}
  </Box>
);

const NameCell = ({ name, email }) => (
  <Box
    sx={{ display: "flex", alignItems: "center", gap: "12px", height: "100%" }}
  >
    <AvatarDot email={email} name={name} />
    <Box
      sx={{
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: "2px",
      }}
    >
      <Box
        sx={{
          fontWeight: 600,
          fontSize: 13.5,
          lineHeight: 1.2,
          color: T.ink900,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {name}
      </Box>
      {email && (
        <Box
          sx={{
            fontSize: 12,
            lineHeight: 1.2,
            color: T.ink500,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {email}
        </Box>
      )}
    </Box>
  </Box>
);

const ActionLink = ({ children, onClick, startIcon }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      font: "inherit",
      fontSize: 12,
      fontWeight: 600,
      lineHeight: 1,
      display: "inline-flex",
      alignItems: "center",
      gap: "5px",
      border: `1px solid ${T.line}`,
      background: "#fff",
      color: T.ink700,
      cursor: "pointer",
      px: "10px",
      py: "6px",
      borderRadius: "7px",
      whiteSpace: "nowrap",
      transition: ".12s",
      "&:hover": {
        color: T.teal600,
        borderColor: T.teal500,
        background: T.teal50,
      },
    }}
  >
    {startIcon}
    {children}
  </Box>
);

const FilterChips = ({ value, onChange, options }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
    {options.map((o) => {
      const on = value === o.value;
      return (
        <Box
          key={o.value}
          component="button"
          type="button"
          onClick={() => onChange(o.value)}
          sx={{
            font: "inherit",
            display: "inline-flex",
            alignItems: "center",
            gap: "7px",
            cursor: "pointer",
            border: `1px solid ${on ? T.teal500 : T.line}`,
            background: on ? T.teal50 : "#fff",
            color: on ? T.teal600 : T.ink700,
            fontWeight: 600,
            fontSize: 12.5,
            borderRadius: "999px",
            px: "12px",
            py: "6px",
            transition: ".12s",
            "&:hover": { borderColor: on ? T.teal500 : T.ink300 },
          }}
        >
          {o.label}
          {o.count != null && (
            <Box
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: on ? T.teal600 : T.ink500,
              }}
            >
              {o.count}
            </Box>
          )}
        </Box>
      );
    })}
  </Box>
);

const SearchBar = ({ value, onChange, placeholder }) => (
  <Box
    sx={{
      position: "relative",
      display: "flex",
      alignItems: "center",
      flex: "1 1 260px",
      maxWidth: 360,
    }}
  >
    <SearchRounded
      sx={{
        position: "absolute",
        left: 12,
        fontSize: 19,
        color: T.ink300,
        zIndex: 1,
      }}
    />
    <InputBase
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      sx={{
        width: "100%",
        height: 40,
        fontSize: 14,
        pl: "40px",
        pr: "12px",
        border: `1px solid ${T.line}`,
        borderRadius: "9px",
        background: "#fff",
        "&.Mui-focused": {
          borderColor: T.teal500,
          boxShadow: `0 0 0 3px rgba(33,169,196,.14)`,
        },
      }}
    />
  </Box>
);

const Footer = ({ summary, page, pageCount, pageSize, onPage, onPageSize }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      px: "4px",
      pt: 1.5,
      fontSize: 12.5,
      color: T.ink500,
      flexWrap: "wrap",
      gap: 1,
    }}
  >
    <Box>{summary}</Box>
    <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        Page size
        <Box
          component="select"
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          sx={{
            font: "inherit",
            fontSize: 12.5,
            color: T.ink700,
            border: `1px solid ${T.line}`,
            borderRadius: "7px",
            px: "8px",
            py: "5px",
            background: "#fff",
            cursor: "pointer",
          }}
        >
          {PAGE_SIZES.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: "4px" }}>
        {pageItems(page, pageCount).map((it, i) =>
          it === "…" ? (
            <Box key={`e${i}`} sx={{ px: "6px", py: "5px", color: T.ink300 }}>
              …
            </Box>
          ) : (
            <Box
              key={it}
              component="button"
              type="button"
              onClick={() => onPage(it)}
              sx={{
                font: "inherit",
                fontSize: 12.5,
                border: `1px solid ${it === page ? T.teal600 : T.line}`,
                background: it === page ? T.teal600 : "#fff",
                color: it === page ? "#fff" : T.ink700,
                fontWeight: it === page ? 700 : 500,
                borderRadius: "6px",
                px: "10px",
                py: "5px",
                cursor: "pointer",
              }}
            >
              {it}
            </Box>
          ),
        )}
        <Box
          component="button"
          type="button"
          onClick={() => onPage(Math.min(pageCount, page + 1))}
          disabled={page >= pageCount}
          sx={{
            font: "inherit",
            fontSize: 12.5,
            border: `1px solid ${T.line}`,
            background: "#fff",
            color: page >= pageCount ? T.ink300 : T.ink700,
            borderRadius: "6px",
            px: "10px",
            py: "5px",
            cursor: page >= pageCount ? "default" : "pointer",
          }}
        >
          ›
        </Box>
      </Box>
    </Box>
  </Box>
);

const GridCard = ({ children }) => (
  <Box
    sx={{
      background: T.card,
      border: `1px solid ${T.line}`,
      borderRadius: T.radius,
      boxShadow: T.shadow,
      overflow: "hidden",
    }}
  >
    {children}
  </Box>
);

const PageShell = ({ children }) => (
  <Box
    sx={{
      background: T.bg,
      minHeight: "100%",
      height: "100%",
      overflowY: "auto",
      fontFamily: "inherit",
    }}
  >
    <Container maxWidth="xl" sx={{ pt: "30px", pb: "64px" }}>
      {children}
    </Container>
  </Box>
);

const centerCell = { display: "flex", alignItems: "center" };
const leftActions = { ...centerCell, justifyContent: "flex-start" };

const gridProps = {
  defaultColDef: {
    resizable: true,
    sortable: true,
    filter: false,
    minWidth: 90,
  },
  domLayout: "autoHeight",
  rowHeight: 62,
  headerHeight: 42,
  suppressCellFocus: true,
  pagination: true,
  suppressPaginationPanel: true,
};

const useGridPagination = (initialSize = 15) => {
  const [gridApi, setGridApi] = useState(null);
  const [pageSize, setPageSize] = useState(initialSize);
  const [pageInfo, setPageInfo] = useState({
    page: 1,
    pageCount: 1,
    rowCount: 0,
  });

  const onGridReady = useCallback((e) => setGridApi(e.api), []);
  const onPaginationChanged = useCallback((e) => {
    if (!e.api) return;
    setPageInfo({
      page: e.api.paginationGetCurrentPage() + 1,
      pageCount: Math.max(1, e.api.paginationGetTotalPages()),
      rowCount: e.api.paginationGetRowCount(),
    });
  }, []);
  const goToPage = useCallback(
    (n) => gridApi?.paginationGoToPage(n - 1),
    [gridApi],
  );
  const goFirst = useCallback(
    () => gridApi?.paginationGoToFirstPage(),
    [gridApi],
  );

  return {
    onGridReady,
    onPaginationChanged,
    pageSize,
    setPageSize,
    pageInfo,
    goToPage,
    goFirst,
  };
};

const ColumnText = ({ children }) => (
  <Box sx={{ color: T.ink900 }}>{children}</Box>
);

// ── master/detail: the borrower's loans, nested AG Grid ──────────────────────
const BorrowerLoansPanel = ({ data, onViewLoan }) => {
  const loans = useMemo(() => deriveLoans(data), [data]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Property",
        field: "propertyAddress",
        flex: 2,
        minWidth: 240,
        cellStyle: centerCell,
        cellRenderer: (p) => (
          <Box
            sx={{
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              // 2px between the address and entity lines — gap is raw px here
              // (this Box isn't a Flx, so MUI's 8× spacing scale doesn't apply).
              gap: "2px",
            }}
          >
            <Box
              sx={{
                fontWeight: 600,
                fontSize: 13,
                lineHeight: 1.2,
                color: T.ink900,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {p.value || "—"}
            </Box>
            {p.data?.entityName && (
              <Box sx={{ fontSize: 12, lineHeight: 1.2, color: T.ink500 }}>
                {p.data.entityName}
              </Box>
            )}
          </Box>
        ),
      },
      {
        headerName: "Product",
        field: "loanProductType",
        flex: 1,
        minWidth: 150,
        cellStyle: centerCell,
        cellRenderer: (p) =>
          p.value ? <Tag label={p.value} tone="teal" /> : <ColumnText>—</ColumnText>,
      },
      {
        headerName: "Purpose",
        field: "loanPurpose",
        flex: 1,
        minWidth: 150,
        cellStyle: centerCell,
        cellRenderer: (p) => <ColumnText>{p.value || "—"}</ColumnText>,
      },
      {
        headerName: "Loan Amount",
        field: "totalLoanAmount",
        flex: 0.9,
        minWidth: 130,
        cellStyle: centerCell,
        valueFormatter: (p) => formatDollar(p.value, "—"),
      },
      {
        headerName: "Rate",
        field: "interestRate",
        flex: 0.6,
        minWidth: 100,
        cellStyle: centerCell,
        valueFormatter: (p) =>
          typeof p.value === "number" ? formatPercent(p.value) : "—",
      },
      {
        headerName: "Status",
        field: "status",
        flex: 0.9,
        minWidth: 130,
        cellStyle: centerCell,
        cellRenderer: (p) =>
          p.value ? <Tag label={p.value} tone="slate" /> : <ColumnText>—</ColumnText>,
      },
      {
        headerName: "Term Sheets",
        field: "termSheetCount",
        flex: 0.8,
        minWidth: 130,
        cellStyle: centerCell,
        cellRenderer: (p) =>
          p.data?.accepted ? (
            <Tag label="Accepted" tone="green" />
          ) : p.value ? (
            <Tag label={`${p.value} pending`} tone="amber" />
          ) : (
            <ColumnText>—</ColumnText>
          ),
      },
      {
        headerName: "",
        flex: 0.9,
        minWidth: 140,
        sortable: false,
        cellStyle: leftActions,
        cellRenderer: (p) => (
          <ActionLink
            onClick={() => onViewLoan(p.data?.dealId)}
            startIcon={<OpenInNewRounded sx={{ fontSize: 14 }} />}
          >
            View Details
          </ActionLink>
        ),
      },
    ],
    [onViewLoan],
  );

  return (
    <Box sx={{ p: 2.5, background: T.bg }}>
      <Box
        sx={{
          fontSize: 12.5,
          fontWeight: 700,
          color: T.ink700,
          mb: 1,
          letterSpacing: ".02em",
          textTransform: "uppercase",
        }}
      >
        Loans · {loans.length}
      </Box>
      {loans.length === 0 ? (
        <Box
          sx={{
            background: "#fff",
            border: `1px dashed ${T.line}`,
            borderRadius: "10px",
            py: 4,
            textAlign: "center",
            color: T.ink500,
            fontSize: 13.5,
          }}
        >
          No loans are linked to this borrower yet.
        </Box>
      ) : (
        <GridCard>
          <AgGridTableWrapper noBorder borderRadius="medium">
            <AgGridReact
              defaultColDef={{ resizable: true, sortable: true, minWidth: 90 }}
              domLayout="autoHeight"
              rowHeight={48}
              headerHeight={40}
              suppressCellFocus
              rowData={loans}
              columnDefs={columnDefs}
              getRowId={(p) => String(p.data?.dealId)}
            />
          </AgGridTableWrapper>
        </GridCard>
      )}
    </Box>
  );
};

// ── screen ───────────────────────────────────────────────────────────────────
export default function BorrowerPortalUsersScreen() {
  useDocumentTitle("Borrower Portal Users");
  const navigate = useNavigate();

  // Fetch-once-then-cache (see useCachedSubmissions.js). Reading from cache on
  // remount — and the inFlight guard inside the hook — is what prevents the
  // refetch loop this screen used to have. An empty/failed read yields [].
  const {
    data: users,
    loading,
    reload,
  } = useCachedBorrowerPortalUsers({ auto: true });

  const [filter, setFilter] = useState(ALL);
  const [search, setSearch] = useState("");
  const [jsonUser, setJsonUser] = useState(null);

  const {
    onGridReady,
    onPaginationChanged,
    pageSize,
    setPageSize,
    pageInfo,
    goToPage,
    goFirst,
  } = useGridPagination(15);

  const chips = useMemo(() => {
    const counts = users.reduce((acc, u) => {
      const s = borrowerStatus(u).label;
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});
    return [
      { value: ALL, label: "All", count: users.length },
      { value: "Active", label: "Active", count: counts.Active || 0 },
      { value: "Invited", label: "Invited", count: counts.Invited || 0 },
      { value: "Staged", label: "Staged", count: counts.Staged || 0 },
    ];
  }, [users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (filter !== ALL && borrowerStatus(u).label !== filter) return false;
      if (q) {
        const hay =
          `${fullName(u)} ${u.emailAddress || ""} ${u.phone || ""} ${asArray(
            u.associatedEntities,
          )
            .map((e) => e.entityName)
            .join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, filter, search]);

  // New filter/search → jump back to the first page of the full result set.
  useEffect(() => goFirst(), [filter, search, goFirst]);

  const onViewLoan = useCallback(
    (dealId) => {
      if (dealId) navigate(`/loan/${dealId}`);
    },
    [navigate],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Borrower",
        field: "emailAddress",
        flex: 1.8,
        minWidth: 260,
        cellStyle: centerCell,
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: {
          suppressCount: true,
          innerRenderer: (p) => (
            <NameCell name={fullName(p.data)} email={p.data?.emailAddress} />
          ),
        },
        valueGetter: (p) => `${fullName(p.data)} ${p.data?.emailAddress || ""}`,
      },
      {
        headerName: "Phone",
        field: "phone",
        flex: 0.9,
        minWidth: 140,
        cellStyle: centerCell,
        cellRenderer: (p) => <ColumnText>{formatPhone(p.value)}</ColumnText>,
      },
      {
        headerName: "Status",
        flex: 0.8,
        minWidth: 120,
        cellStyle: centerCell,
        valueGetter: (p) => borrowerStatus(p.data).label,
        cellRenderer: (p) => {
          const s = borrowerStatus(p.data);
          return <Tag label={s.label} tone={s.tone} />;
        },
      },
      {
        headerName: "Loans",
        flex: 0.6,
        minWidth: 100,
        cellStyle: centerCell,
        valueGetter: (p) => loanCount(p.data),
        cellRenderer: (p) =>
          p.value ? (
            <Tag label={`${p.value} loan${p.value === 1 ? "" : "s"}`} tone="teal" />
          ) : (
            <Box component="span" sx={{ color: T.ink300 }}>
              —
            </Box>
          ),
      },
      {
        headerName: "Entities",
        flex: 1.3,
        minWidth: 180,
        cellStyle: centerCell,
        valueGetter: (p) =>
          asArray(p.data?.associatedEntities)
            .map((e) => e.entityName)
            .join(", "),
        cellRenderer: (p) => {
          const names = asArray(p.data?.associatedEntities)
            .map((e) => e.entityName)
            .filter(Boolean);
          return (
            <TagCell
              title={names.join(", ")}
              tags={names.map((n) => (
                <Tag key={n} label={n} tone="indigo" />
              ))}
            />
          );
        },
      },
      {
        headerName: "Invited By",
        field: "invitedBy",
        flex: 1.1,
        minWidth: 170,
        cellStyle: centerCell,
        cellRenderer: (p) => (
          <ColumnText>{p.value || p.data?.createdBy || "—"}</ColumnText>
        ),
      },
      {
        headerName: "Last Sign In",
        field: "lastSignInDate",
        flex: 0.9,
        minWidth: 140,
        cellStyle: centerCell,
        comparator: (a, b) => dateMs(a) - dateMs(b),
        cellRenderer: (p) =>
          p.value ? (
            <span title={formatDateTime(p.value)}>{timeAgo(p.value)}</span>
          ) : (
            <Box component="span" sx={{ color: T.ink300 }}>
              Never
            </Box>
          ),
      },
      {
        headerName: "Created",
        field: "createdDate",
        flex: 0.8,
        minWidth: 120,
        cellStyle: centerCell,
        sort: "desc",
        valueGetter: (p) => p.data?.createdDate || p.data?.createDateTime,
        comparator: (a, b) => dateMs(a) - dateMs(b),
        valueFormatter: (p) => (p.value ? formatDateTime(p.value, "date") : "—"),
      },
      {
        headerName: "Actions",
        flex: 0.7,
        minWidth: 120,
        sortable: false,
        cellStyle: leftActions,
        cellRenderer: (p) => (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: "6px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <ActionLink onClick={() => setJsonUser(p.data)}>JSON</ActionLink>
          </Box>
        ),
      },
    ],
    [],
  );

  const detailCellRendererParams = useMemo(
    () => ({ onViewLoan }),
    [onViewLoan],
  );

  const total = filtered.length;
  const start = (pageInfo.page - 1) * pageSize;
  const end = Math.min(start + pageSize, total);
  const summary = total
    ? `${start + 1}–${end} of ${total} borrower${total === 1 ? "" : "s"}`
    : "0 borrowers";

  return (
    <PageShell>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Box
            sx={{
              fontSize: 23,
              fontWeight: 700,
              letterSpacing: "-.01em",
              color: T.ink900,
            }}
          >
            Borrower Portal Users
          </Box>
          <Box sx={{ color: T.ink500, mt: "5px", fontSize: 13.5 }}>
            Everyone with borrower-portal access. Expand a row to see their loans
            and jump into any one.
          </Box>
        </Box>
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.25 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshRounded sx={{ fontSize: 18 }} />}
            onClick={reload}
            sx={{
              color: T.ink700,
              borderColor: T.line,
              background: "#fff",
              fontWeight: 600,
              borderRadius: "8px",
              "&:hover": { borderColor: T.ink300, background: "#f8fafb" },
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mt: 3,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <FilterChips value={filter} onChange={setFilter} options={chips} />
        <Box sx={{ ml: "auto" }}>
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search name, email, phone, entity…"
          />
        </Box>
      </Box>

      <GridCard>
        {!loading && filtered.length === 0 ? (
          <Box
            sx={{
              display: "grid",
              placeItems: "center",
              textAlign: "center",
              py: 7,
              px: 4,
            }}
          >
            <Box sx={{ fontSize: 17, fontWeight: 700, color: T.ink900 }}>
              No borrowers match
            </Box>
            <Box sx={{ fontSize: 13.5, color: T.ink500, mt: 0.5 }}>
              Try a different filter or search term.
            </Box>
          </Box>
        ) : (
          <AgGridTableWrapper noBorder borderRadius="medium">
            <AgGridReact
              {...gridProps}
              rowData={filtered}
              columnDefs={columnDefs}
              getRowId={(p) => userId(p.data)}
              loading={loading && !users.length}
              paginationPageSize={pageSize}
              onGridReady={onGridReady}
              onPaginationChanged={onPaginationChanged}
              masterDetail
              isRowMaster={(d) => loanCount(d) > 0}
              detailCellRenderer={BorrowerLoansPanel}
              detailCellRendererParams={detailCellRendererParams}
              detailRowAutoHeight
              keepDetailRows
              rowStyle={{ cursor: "pointer" }}
              onRowClicked={(e) => {
                if (loanCount(e.data) > 0) e.node.setExpanded(!e.node.expanded);
              }}
            />
          </AgGridTableWrapper>
        )}
      </GridCard>

      <Footer
        summary={summary}
        page={pageInfo.page}
        pageCount={pageInfo.pageCount}
        pageSize={pageSize}
        onPage={goToPage}
        onPageSize={setPageSize}
      />

      <SideDrawer
        show={Boolean(jsonUser)}
        onClose={() => setJsonUser(null)}
        title={
          jsonUser
            ? `Borrower JSON — ${jsonUser.emailAddress || ""}`
            : "Borrower JSON"
        }
        drawerWidth="640px"
      >
        {jsonUser && <JsonPreview defaultCollapse={3} values={jsonUser} />}
      </SideDrawer>
    </PageShell>
  );
}
